import { RouteLocationRaw } from 'vue-router';

import { OrderStep } from '@/constants/order/constants';
import { socketDetailsEntryStep } from '@/constants/order/nav/entrySteps';
import { OrderSectionNavTabModel } from '@/store/models/OrderNavTabsItem';
import { orderGetters } from '@/store/modules/order';
import { OrderStatusRejected } from '@/store/modules/order/types';

import { OrderSection, orderSections } from '../sections/constants';

export enum OrderSectionsFlow {
	Customization = 'customization',
	Prosthetist = 'prosthetist',
	SocketFitting = 'socket_fitting',
	Edit = 'edit',
}

const toFactory =
	(orderGuid: string) =>
	(name: string): RouteLocationRaw => ({
		name,
		params: { orderGuid },
	});
const navItemFactory =
	(orderGuid: string) =>
	(section: OrderSection, label: string): OrderSectionNavTabModel => {
		const step = orderSections[section][0];

		return {
			label,
			to: toFactory(orderGuid)(step),
			matched_routes: [step],
			steps: [step],
		};
	};

function customizationSections(orderGuid: string): Array<OrderSectionNavTabModel> {
	const getTo = toFactory(orderGuid);
	const getNavItem = navItemFactory(orderGuid);

	return [
		{
			to: { name: OrderStep.New },
			matched_routes: [OrderStep.New],
			label: 'Type of Prosthesis',
			steps: [],
		},
		{
			to: getTo('order-customization'),
			matched_routes: ['order-customization', ...orderSections[OrderSection.Customize]],
			label: 'Customizer',
			steps: [...orderSections[OrderSection.Customize]],
		},
		getNavItem(OrderSection.ClientsDetails, 'Clients Details'),
		// getNavItem(OrderSection.SocketDetails, 'Socket Details'),
		{
			to: getTo(socketDetailsEntryStep(orderGetters.getOrderByGuid()(orderGuid))),
			matched_routes: ['socket-details', ...orderSections[OrderSection.SocketDetails]],
			label: 'Socket Details',
			steps: [...orderSections[OrderSection.SocketDetails]],
		},
		getNavItem(OrderSection.OrderPreview, 'Order Confirm'),
	];
}

function prosthetistSections(orderGuid: string): Array<OrderSectionNavTabModel> {
	const getNavItem = navItemFactory(orderGuid);
	const getTo = toFactory(orderGuid);

	const order = orderGetters.getOrderByGuid()(orderGuid);

	if (order?.terminal_device_only) {
		return [getNavItem(OrderSection.OrderFinalise, 'Order Preview')];
	}

	const data = [
		{
			to: getTo('order-scan'),
			matched_routes: ['order-scan', ...orderSections[OrderSection.Scan]],
			label: 'Adding Scan',
			steps: [...orderSections[OrderSection.Scan]],
		},
	];

	if (order?.prosthesis?.prosthesis_category.slug == 'glaze-below') {
		data.push(getNavItem(OrderSection.Alignment, 'Alignment'));
	}

	if (order?.socket_detail_order?.include_petg_socket) {
		data.push(getNavItem(OrderSection.Details, 'Details'));
	} else {
		data.push(getNavItem(OrderSection.Files, 'Files'));
	}
	data.push(getNavItem(OrderSection.OrderFinalise, 'Order Preview'));

	return data;
}

function socketFittingSections(orderGuid: string): Array<OrderSectionNavTabModel> {
	const getNavItem = navItemFactory(orderGuid);
	const order = orderGetters.getOrderByGuid()(orderGuid);

	const data = [];
	data.push(getNavItem(OrderSection.Feedback, 'Feedback'));

	if (
		order?.socket_detail_order?.petg_has_changes ||
		order?.prosthesis?.prosthesis_category?.slug == 'glaze-above'
	) {
		data.push(getNavItem(OrderSection.SocketFittingFiles, 'Adding scan'));
	}

	data.push(getNavItem(OrderSection.SocketFittingFinalise, 'Summary of order'));

	return data;
}

enum RejectedStatuses {
	IncorrectMeasurements = 'rejected_incorrect_measurements',
	IncorrectAttachments = 'rejected_incorrect_attachments',
	IncorrectScan = 'rejected_incorrect_scan',
}

function editSections(orderGuid: string): Array<OrderSectionNavTabModel> {
	const getNavItem = navItemFactory(orderGuid);
	const order = orderGetters.getOrderByGuid()(orderGuid);

	if (!order) return [];

	const orderStatus = order.order_status as OrderStatusRejected;

	if (orderStatus === RejectedStatuses.IncorrectMeasurements) {
		return [getNavItem(OrderSection.ClientsDetails, 'Clients Details')];
	}

	if (orderStatus === RejectedStatuses.IncorrectAttachments) {
		return [getNavItem(OrderSection.Files, 'Files')];
	}

	if (orderStatus === RejectedStatuses.IncorrectScan) {
		return [getNavItem(OrderSection.Scan, 'Adding Scan')];
	}

	return [];
}

export function getOrderSectionsNav(
	orderGuid: string,
	flow: OrderSectionsFlow,
): Array<OrderSectionNavTabModel> {
	switch (flow) {
		case OrderSectionsFlow.Customization:
			return customizationSections(orderGuid);
		case OrderSectionsFlow.Prosthetist:
			return prosthetistSections(orderGuid);
		case OrderSectionsFlow.SocketFitting:
			return socketFittingSections(orderGuid);
		case OrderSectionsFlow.Edit:
			return editSections(orderGuid);
	}
}
